/**
 * Real `fetch`-backed HttpTransport (S097; M12). The framework-neutral apps inject this at boot — it is the one
 * adapter that turns the typed `ApiClient`'s `HttpRequest` into an actual network call on any runtime exposing a
 * WHATWG `fetch`: Node 18+, React Native / Expo, and the browser / Next.js. It enforces the per-request deadline
 * with an `AbortController` and maps every failure onto the two-kind `HttpTransportError` the client understands
 * (`timeout` vs `network`), so no runtime-specific error class leaks into the domain. `fetch` and
 * `AbortController` are injectable so the adapter itself is deterministically unit-testable with no real socket.
 */
import { HttpTransportError } from './transport.js';
export class FetchHttpTransport {
    fetchImpl;
    abortCtor;
    defaultTimeoutMs;
    constructor(options = {}) {
        const g = globalThis;
        const resolved = options.fetch ?? g.fetch;
        if (resolved === undefined) {
            throw new Error('FetchHttpTransport: no fetch available on this runtime; pass options.fetch.');
        }
        // Bind the resolved `fetch` to the global object. The WHATWG `fetch` is a brand-checked method of
        // `Window`/`WorkerGlobalScope`: invoking it as a method of any *other* object — which the `this.fetchImpl(…)`
        // call in `send` does (`this` = the transport instance) — throws
        // `TypeError: Failed to execute 'fetch' on 'Window': Illegal invocation` in the browser. That throw was
        // caught in `send` and mislabeled a `network` failure, so every real-browser operator login died *before a
        // request left the page* (a "could not reach the API" with no HTTP request). Unit tests never caught it —
        // an injected plain-function fake `fetch` ignores its `this`. Binding once here makes every call `this`-safe
        // across the browser, React Native, and Node globals alike.
        this.fetchImpl = resolved.bind(g);
        this.abortCtor = options.abortController ?? g.AbortController;
        this.defaultTimeoutMs = options.defaultTimeoutMs;
    }
    async send(req) {
        const timeoutMs = req.timeoutMs ?? this.defaultTimeoutMs;
        const controller = timeoutMs !== undefined && this.abortCtor !== undefined ? new this.abortCtor() : undefined;
        const timer = controller !== undefined && timeoutMs !== undefined
            ? setTimeout(() => { controller.abort(); }, timeoutMs)
            : undefined;
        let res;
        try {
            res = await this.fetchImpl(req.url, {
                method: req.method,
                headers: { ...req.headers },
                ...(req.body !== undefined ? { body: req.body } : {}),
                ...(controller !== undefined ? { signal: controller.signal } : {}),
            });
        }
        catch (e) {
            // An abort (the deadline elapsed) surfaces as a timeout; anything else is a connectivity failure.
            if (isAbortError(e))
                throw new HttpTransportError('timeout');
            throw new HttpTransportError('network', e instanceof Error ? e.message : undefined);
        }
        finally {
            if (timer !== undefined)
                clearTimeout(timer);
        }
        const headers = {};
        res.headers.forEach((value, key) => {
            headers[key.toLowerCase()] = value;
        });
        const body = await res.text();
        return { status: res.status, headers, body };
    }
    /** D532: the same request answered with the body's raw bytes (a file download). */
    async sendBytes(req) {
        const timeoutMs = req.timeoutMs ?? this.defaultTimeoutMs;
        const controller = timeoutMs !== undefined && this.abortCtor !== undefined ? new this.abortCtor() : undefined;
        const timer = controller !== undefined && timeoutMs !== undefined ? setTimeout(() => { controller.abort(); }, timeoutMs) : undefined;
        let res;
        try {
            res = await this.fetchImpl(req.url, {
                method: req.method,
                headers: { ...req.headers },
                ...(req.body !== undefined ? { body: req.body } : {}),
                ...(controller !== undefined ? { signal: controller.signal } : {}),
            });
            const headers = {};
            res.headers.forEach((value, key) => {
                headers[key.toLowerCase()] = value;
            });
            // The deadline covers the body too: a large file is read before the timer is cleared.
            const bytes = res.arrayBuffer !== undefined ? new Uint8Array(await res.arrayBuffer()) : new TextEncoder().encode(await res.text());
            return { status: res.status, headers, bytes };
        }
        catch (e) {
            if (isAbortError(e))
                throw new HttpTransportError('timeout');
            throw new HttpTransportError('network', e instanceof Error ? e.message : undefined);
        }
        finally {
            if (timer !== undefined)
                clearTimeout(timer);
        }
    }
}
function isAbortError(e) {
    return typeof e === 'object' && e !== null && e.name === 'AbortError';
}
//# sourceMappingURL=fetch-transport.js.map